/**
 * Peer Authentication for Captain Mesh Transport
 */
import { importPublicKey, verifySignature } from "./identity";

/**
 * Validates a peer's authorization record and their signature over a challenge.
 * Returns true if the peer is authenticated and authorized for the specified mesh.
 */
export async function authenticatePeerConnection(
  expectedRestaurantId,
  challengeString,
  peerAuthRecord,
  peerSignatureBase64
) {
  if (!peerAuthRecord || !peerSignatureBase64) return false;

  // 1. Verify that the server authorization is for the correct restaurant
  if (peerAuthRecord.restaurant_id !== expectedRestaurantId) {
    console.warn(`[MeshAuth] Rejecting peer: restaurant ID mismatch. Expected ${expectedRestaurantId}, got ${peerAuthRecord.restaurant_id}`);
    return false;
  }

  // 2. Verify the server's signature over the authorization record
  const serverKeyJwk = await getMeshPublicKeyJwk();
  if (!serverKeyJwk) {
    console.error("[MeshAuth] No mesh_public_key_jwk cached — cannot verify peer authorization.");
    return false;
  }

  if (!peerAuthRecord.public_key_jwk) {
    return false;
  }

  const isValidAuth = await verifyServerAuthorization(peerAuthRecord, serverKeyJwk);
  if (!isValidAuth) {
    console.warn(`[MeshAuth] Rejecting peer: Invalid server authorization signature for ${peerAuthRecord.device_id}`);
    return false;
  }

  // 3. Import the peer's public key
  let peerPublicKey;
  try {
    peerPublicKey = await importPublicKey(peerAuthRecord.public_key_jwk);
  } catch (err) {
    console.error("[MeshAuth] Failed to import peer public key", err);
    return false;
  }

  // 4. Verify that the peer possesses the corresponding private key
  // by verifying their signature over the challenge we sent them.
  const isValid = await verifySignature(peerPublicKey, peerSignatureBase64, challengeString);
  
  if (!isValid) {
    console.warn(`[MeshAuth] Rejecting peer: invalid challenge signature for device ${peerAuthRecord.device_id}`);
    return false;
  }

  return true;
}

/**
 * Creates a cryptographically strong challenge string
 */
export function createChallenge() {
  const array = new Uint8Array(32);
  window.crypto.getRandomValues(array);
  return Array.from(array, b => b.toString(16).padStart(2, "0")).join("");
}

// Helpers for Server Authorization Verification
import { offlineDb } from "@/db/offlineDb";

async function getMeshPublicKeyJwk() {
  const setting = await offlineDb.settings.get("mesh_public_key_jwk");
  return setting ? setting.value : null;
}

/**
 * RFC 7638 thumbprint of an EC JWK, mirroring
 * backend/app/services/mesh_signing_service.py::jwk_thumbprint.
 *
 * Only the four required members participate, in lexicographic order, so a peer
 * cannot change the digest by padding its JWK with extra fields.
 */
async function jwkThumbprint(jwk) {
  const canonical = `{"crv":${JSON.stringify(jwk.crv)},"kty":${JSON.stringify(jwk.kty)},` +
    `"x":${JSON.stringify(jwk.x)},"y":${JSON.stringify(jwk.y)}}`;
  const digest = await window.crypto.subtle.digest("SHA-256", new TextEncoder().encode(canonical));
  const bytes = new Uint8Array(digest);
  let binary = "";
  for (let i = 0; i < bytes.byteLength; i++) binary += String.fromCharCode(bytes[i]);
  return window.btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

/**
 * Verifies that the server issued this authorization, using the server's PUBLIC
 * key. The device holds no key that could produce such a signature — that
 * asymmetry is the point, and the reason this is no longer an HMAC.
 *
 * The signed payload includes the thumbprint of the device's own public key, so
 * lifting a valid record and substituting a different key fails here rather than
 * granting the attacker the victim's role.
 */
async function verifyServerAuthorization(record, serverKeyJwk) {
  if (!record.server_signature) return false;

  let serverKey;
  let thumbprint;
  try {
    serverKey = await importPublicKey(serverKeyJwk);
    thumbprint = await jwkThumbprint(record.public_key_jwk);
  } catch (err) {
    console.error("[MeshAuth] Could not prepare server-signature verification", err);
    return false;
  }

  const payload = [
    record.restaurant_id,
    record.mesh_id,
    record.device_id,
    record.role,
    record.user_id,
    record.issued_at,
    thumbprint,
  ].join(":");

  return await verifySignature(serverKey, record.server_signature, payload);
}
